// This component is used to handle the effects required for the app

/*
 * We split each effect into its own memoized component for several reasons:
 * 1. Isolation: Each effect runs independently, preventing side effects from affecting each other
 * 2. Performance: Using memo with () => true ensures each effect component only mounts once
 *    and never re-renders, even if the parent re-renders
 * 3. Maintainability: Easier to add/remove effects without affecting others
 * 4. Debugging: Simpler to track which effect might be causing issues
 */

import { type FC, memo } from 'react'
import { useAppEffects } from '../hooks/use-app.effects'
import { useInactivityManager } from '../hooks/use-inactivity-manager'
import { usePubNubEffects } from '../hooks/use-pubnub.effects'
import { useTabManager } from '../hooks/use-tab-manager'

// HOC to create effect components
export const createEffectComponent = (effect: () => void, displayName: string): FC => {
  const EffectComponent = memo(
    () => {
      effect()
      return null
    },
    () => true,
  )

  EffectComponent.displayName = displayName
  return EffectComponent
}

const TabManagerEffects = createEffectComponent(useTabManager, 'TabManagerEffects')
const AppEffects = createEffectComponent(useAppEffects, 'AppEffects')
const PubNubEffects = createEffectComponent(usePubNubEffects, 'PubNubEffects')
const InactivityEffects = createEffectComponent(useInactivityManager, 'InactivityEffects')
// Mostly we try to collate all global effects here
export const EffectsHandler = memo(
  () => (
    <>
      <InactivityEffects />
      <TabManagerEffects />
      <AppEffects />
      <PubNubEffects />
    </>
  ),
  () => true,
)

EffectsHandler.displayName = 'EffectsHandler'
